import { useRef } from 'react';

import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

interface EmojiPickerInputProps {
  value: string;
  onChange: (emoji: string) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  isDisabled?: boolean;
}

interface EmojiMartSelection {
  native?: string;
}

/**
 * Emoji selector backed by emoji-mart. The picker only ever yields a single
 * valid native emoji, so the stored value is guaranteed well-formed; the
 * back-end emoji validator remains the authoritative backstop.
 */
function EmojiPickerInput({
  value,
  onChange,
  isInvalid = false,
  errorMessage = 'Select a valid emoji.',
  isDisabled = false,
}: EmojiPickerInputProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialFocusRef = useRef<HTMLDivElement>(null);

  const handleSelect = (emoji: EmojiMartSelection) => {
    if (emoji.native) {
      onChange(emoji.native);
    }
    onClose();
  };

  return (
    <FormControl isInvalid={isInvalid} maxW="120px">
      <FormLabel>Icon</FormLabel>
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom-start"
        initialFocusRef={initialFocusRef}
        isLazy
      >
        <PopoverTrigger>
          <Button
            variant="outline"
            h="48px"
            w="64px"
            fontSize="24px"
            isDisabled={isDisabled}
            aria-label="Choose an emoji"
            data-testid="emoji-picker-trigger"
          >
            {value ? (
              <Text as="span" fontSize="24px" lineHeight="1">
                {value}
              </Text>
            ) : (
              <Text as="span" fontSize="20px" color="brand.darkGray">
                +
              </Text>
            )}
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent w="auto" boxShadow="lg" zIndex="popover">
            <PopoverBody p="0">
              <Box ref={initialFocusRef}>
                <Picker
                  data={data}
                  onEmojiSelect={handleSelect}
                  previewPosition="none"
                  skinTonePosition="none"
                  navPosition="bottom"
                  maxFrequentRows={1}
                />
              </Box>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
}

export default EmojiPickerInput;
